import { useEffect, useRef, useState } from 'react';
import { HubConnection } from '@microsoft/signalr';
import { signalRConnectionManager } from './signalRConnectionManager';
import { HubClientMethods, HubEnvelope, HubConnectionState as AppHubState, SignalRProtocol } from './types';

export type UseSignalRHubOptions = {
    hubUrl: string;
    protocol?: SignalRProtocol;
    /** Unique key for reconnect re-join registry (e.g. room id). */
    groupKey: string;
    enabled?: boolean;
    join: (connection: HubConnection) => Promise<void>;
    leave?: (connection: HubConnection) => Promise<void>;
    onEvent?: (envelope: HubEnvelope) => void;
    eventHandlers?: Record<string, (payload: unknown, envelope: HubEnvelope) => void>;
};

export type UseSignalRHubResult = {
    connection: HubConnection | null;
    connectionState: AppHubState;
    lastError: string | null;
    invoke: <T = void>(methodName: string, ...args: unknown[]) => Promise<T>;
};

/** Prevents Strict Mode remount leave() from kicking the newer join out of the group. */
const joinEpochByGroup = new Map<string, number>();

function mapState(state: number): AppHubState {
    // HubConnectionState enum: Disconnected=0, Connecting=1, Connected=2, Disconnecting=3, Reconnecting=4
    switch (state) {
        case 1:
            return 'connecting';
        case 2:
            return 'connected';
        case 4:
            return 'reconnecting';
        default:
            return 'disconnected';
    }
}

export function useSignalRHub(options: UseSignalRHubOptions): UseSignalRHubResult {
    const {
        hubUrl,
        protocol = 'json',
        groupKey,
        enabled = true,
        join,
        leave,
        onEvent,
        eventHandlers,
    } = options;

    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [connectionState, setConnectionState] = useState<AppHubState>('disconnected');
    const [lastError, setLastError] = useState<string | null>(null);

    const joinRef = useRef(join);
    const leaveRef = useRef(leave);
    const onEventRef = useRef(onEvent);
    const eventHandlersRef = useRef(eventHandlers);
    const sessionRef = useRef(0);

    joinRef.current = join;
    leaveRef.current = leave;
    onEventRef.current = onEvent;
    eventHandlersRef.current = eventHandlers;

    useEffect(() => {
        if (!enabled || !hubUrl || !groupKey) {
            return;
        }

        const session = ++sessionRef.current;
        let hubConnection: HubConnection | null = null;
        let joinEpoch = 0;
        let receiveHandler: ((envelope: HubEnvelope) => void) | null = null;

        const isCurrentSession = () => session === sessionRef.current;

        const handleReceive = (envelope: HubEnvelope) => {
            const normalized: HubEnvelope = {
                type: (envelope as any)?.type ?? (envelope as any)?.Type,
                payload: (envelope as any)?.payload ?? (envelope as any)?.Payload,
                timestampUtc: (envelope as any)?.timestampUtc ?? (envelope as any)?.TimestampUtc,
            };

            if (!normalized.type) {
                console.warn('[SignalR] Receive without type', envelope);
                return;
            }

            onEventRef.current?.(normalized);
            const handler = eventHandlersRef.current?.[normalized.type];
            if (!handler) {
                console.debug('[SignalR] no handler for', normalized.type);
                return;
            }
            handler(normalized.payload, normalized);
        };

        const start = async () => {
            try {
                if (!isCurrentSession()) return;

                setLastError(null);
                setConnectionState('connecting');
                hubConnection = await signalRConnectionManager.acquire(hubUrl, protocol);
                if (!isCurrentSession()) {
                    await signalRConnectionManager.release(hubUrl, protocol);
                    hubConnection = null;
                    return;
                }

                receiveHandler = handleReceive;
                hubConnection.on(HubClientMethods.Receive, handleReceive);

                const joinHandler = async () => {
                    await joinRef.current(hubConnection!);
                    joinEpoch = (joinEpochByGroup.get(groupKey) || 0) + 1;
                    joinEpochByGroup.set(groupKey, joinEpoch);
                };

                signalRConnectionManager.registerJoinHandler(hubUrl, groupKey, joinHandler, protocol);
                await joinHandler();

                if (!isCurrentSession()) {
                    // Newer effect owns the room — do not leave (epoch check in cleanup).
                    return;
                }

                setConnection(hubConnection);
                setConnectionState(mapState(hubConnection.state));

                hubConnection.onreconnecting(() => {
                    if (isCurrentSession()) setConnectionState('reconnecting');
                });
                hubConnection.onreconnected(() => {
                    if (isCurrentSession()) setConnectionState('connected');
                });
                hubConnection.onclose(() => {
                    if (isCurrentSession()) setConnectionState('disconnected');
                });
            } catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                console.error('[SignalR] connection failed', { hubUrl, err });
                if (isCurrentSession()) {
                    setLastError(message);
                    setConnectionState('disconnected');
                    setConnection(null);
                }
            }
        };

        void start();

        return () => {
            const cleanupConnection = hubConnection;
            const cleanupEpoch = joinEpoch;
            const cleanupReceive = receiveHandler;

            void (async () => {
                if (cleanupConnection && cleanupReceive) {
                    cleanupConnection.off(HubClientMethods.Receive, cleanupReceive);
                }

                const latestEpoch = joinEpochByGroup.get(groupKey) || 0;
                const shouldLeave = cleanupEpoch > 0 && cleanupEpoch === latestEpoch;

                if (shouldLeave && cleanupConnection) {
                    try {
                        await leaveRef.current?.(cleanupConnection);
                    } catch (err) {
                        console.warn('[SignalR] leave failed', err);
                    }
                }

                signalRConnectionManager.unregisterJoinHandler(hubUrl, groupKey, protocol);
                await signalRConnectionManager.release(hubUrl, protocol);

                // Only clear React state if no newer effect took over.
                if (session === sessionRef.current) {
                    setConnection(null);
                    setConnectionState('disconnected');
                }
            })();
        };
    }, [hubUrl, protocol, groupKey, enabled]);

    const invoke = async <T = void>(methodName: string, ...args: unknown[]): Promise<T> => {
        if (!connection) {
            throw new Error('SignalR connection is not ready');
        }
        return connection.invoke<T>(methodName, ...args);
    };

    return { connection, connectionState, lastError, invoke };
}
